import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { formatAmountWithCurrency } from '../../utils/stringUtils';
import Lottie from 'lottie-react';
import emptyStateAnimation from '../../Animations/settings-animation.json';

export default function BudgetDonutChart({ monthData, currency, showHeader = true, showCard = true, size = 'default' }) {
  const [activeIndex, setActiveIndex] = useState(null);
  
  // Get values from monthData or defaults
  const totalIncome = Number(monthData?.totalIncome) || 
    (monthData?.income ? monthData.income.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0);

  const needsAmount = Number(monthData?.totalNeeds) || 
    (monthData?.needs ? monthData.needs.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0);

  const savingsAmount = Number(monthData?.totalSavings) || 
    (monthData?.savings ? monthData.savings.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0);

  const wantsAmount = Number(monthData?.totalWants) || 
    (monthData?.wants ? monthData.wants.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0);

  // Calculate percentages
  const needsPercentage = totalIncome > 0 ? Math.round((needsAmount / totalIncome) * 100) : 0;
  const savingsPercentage = totalIncome > 0 ? Math.round((savingsAmount / totalIncome) * 100) : 0;
  const wantsPercentage = totalIncome > 0 ? Math.round((wantsAmount / totalIncome) * 100) : 0;

  // Prepare data for the chart
  const data = [
    {
      name: 'Needs',
      value: needsAmount,
      percentage: needsPercentage,
      color: 'rgb(252, 186, 137)' // accent-1 (peach/orange)
    },
    {
      name: 'Savings & Investments',
      value: savingsAmount,
      percentage: savingsPercentage,
      color: 'rgb(180, 99, 235)' // accent-3 (purple)
    },
    {
      name: 'Wants',
      value: wantsAmount,
      percentage: wantsPercentage,
      color: 'rgb(92, 195, 250)' // accent-4 (blue)
    }
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 card-base-border rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{data.name}</p>
          <p className="text-sm text-gray-600">
            Amount: {formatAmountWithCurrency(data.value, currency)}
          </p>
          <p className="text-sm text-gray-600">
            Percentage: {data.percentage}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend
  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex flex-row gap-2 pb-4">
        {data.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm md:text-xs">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="">{entry.name}</span>
          </div>
        ))}
      </div>
    );
  };

  // Determine dimensions based on size prop
  const getChartDimensions = () => {
    if (size === 'large') {
      return { height: 'h-72', innerRadius: 70, outerRadius: 110 };
    }
    if (showCard) {
      return { height: 'h-64 mb-4', innerRadius: 60, outerRadius: 100 };
    }
    return { height: 'h-40', innerRadius: 40, outerRadius: 70 };
  };

  const dimensions = getChartDimensions();

  // Chart content (reusable)
  const chartContent = (
    <div className="flex flex-col items-center justify-center w-full">
      <div className={`w-full ${dimensions.height} relative`}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={dimensions.innerRadius}
              outerRadius={dimensions.outerRadius}
              paddingAngle={2}
              dataKey="value"
              onMouseEnter={(data, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  // Empty state content
  const emptyStateContent = (
    <div className="flex flex-col items-center justify-center flex-1 p-6 gap-4">
      <Lottie
        animationData={emptyStateAnimation}
        loop={true}
        autoplay={true}
        style={{ width: 150, height: 150, maxHeight: 150 }}
      />
      <div className="hidden lg:flex flex-col items-center gap-2">
        <p className="text-lg font-semibold">No data to display</p>
        <p className="text-sm text-center color-text-secondary">Add income and expenses to see your budget visualization</p>
      </div>
    </div>
  );

  // Don't render if no data
  const hasData = totalIncome > 0 && (needsAmount > 0 || savingsAmount > 0 || wantsAmount > 0);

  // If not showing card, return just the chart or empty state
  if (!showCard) {
    return hasData ? chartContent : emptyStateContent;
  }

  // Full card version
  return (
    <div className="w-full h-full card-base-border card-base card-base-background flex flex-col">
      {/* Header */}
      {showHeader && (
        <div className="p-3 card-base-border-bottom">
          <h3 className="text-xl font-semibold">Budget visualization</h3>
        </div>
      )}

      {/* Chart Container */}
      {hasData ? (
        <div className="flex flex-col items-center justify-center">
          {chartContent}
          {/* Legend */}
          <CustomLegend payload={data} />
        </div>
      ) : (
        emptyStateContent
      )}
    </div>
  );
}
